import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { CalendarView } from './CalendarView';
import { TimeSlotPicker } from './TimeSlotPicker';
import { PixQRCode } from '@/components/payments/PixQRCode';
import { Captcha } from '@/components/common/Captcha';
import {
  useDoctorAvailabilityCalendar,
  useAvailableSlots,
  useCreateAppointment,
  useAuth,
} from '@/hooks';
import { paymentsApi } from '@/api/payments-api';
import { patientsApi, type DoctorDetail, type PatientProfile, type DependentResponse } from '@/api';
import type { PaymentResult } from '@/api/payments-api';
import {
  Video,
  Building2,
  Home,
  AlertCircle,
  CreditCard,
  Loader2,
  CheckCircle2,
  User,
  Calendar as CalendarIcon,
  Clock,
  ArrowLeft,
  ArrowRight,
  X,
  ShieldCheck,
  Check,
} from 'lucide-react';
import { cn } from '@/utils/cn';

function calculateAge(birthDate: string | Date | null | undefined): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
}

export type ModalityType = 'TELEMEDICINE' | 'IN_PERSON' | 'HOME';
export type ServiceType = 'TELEMEDICINA' | 'DOMICILIAR' | 'PRESENCIAL';

export interface BookAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctor: DoctorDetail;
}

const MODALITIES: {
  id: ModalityType;
  label: string;
  description: string;
  icon: typeof Video;
  serviceType: ServiceType;
}[] = [
  {
    id: 'TELEMEDICINE',
    label: 'Telemedicina',
    description: 'Consulta online por vídeo',
    icon: Video,
    serviceType: 'TELEMEDICINA',
  },
  {
    id: 'IN_PERSON',
    label: 'Presencial',
    description: 'Atendimento no consultório',
    icon: Building2,
    serviceType: 'PRESENCIAL',
  },
  {
    id: 'HOME',
    label: 'Domiciliar',
    description: 'Atendimento em domicílio',
    icon: Home,
    serviceType: 'DOMICILIAR',
  },
];

type PaymentMethod = 'pix' | 'card';

export function BookAppointmentModal({
  isOpen,
  onClose,
  doctor,
}: BookAppointmentModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const createAppointment = useCreateAppointment();

  // Stepper state: 1: Paciente & Modalidade, 2: Data & Horário, 3: Revisão & Pagamento
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [modality, setModality] = useState<ModalityType>('TELEMEDICINE');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [patientProfile, setPatientProfile] = useState<PatientProfile | null>(null);
  const [dependents, setDependents] = useState<DependentResponse[]>([]);
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
  const [attendeeType, setAttendeeType] = useState<'self' | 'dependent'>('self');
  const [selectedDependentId, setSelectedDependentId] = useState<string>('');

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pix');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{
    paymentId: string;
    qrCode?: string;
    qrCodeUrl?: string;
    copyPaste?: string;
  } | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [newAppointmentId, setNewAppointmentId] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const paymentPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [selectedInstallment, setSelectedInstallment] = useState<number>(1);

  const today = new Date().toISOString().split('T')[0] ?? '';
  const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0] ?? '';

  const { data: calendarData, isLoading: isLoadingCalendar } = useDoctorAvailabilityCalendar(
    doctor.id,
    today,
    thirtyDaysFromNow
  );

  const { data: slotsData, isLoading: isLoadingSlots } = useAvailableSlots(
    doctor.id,
    selectedDate ?? ''
  );

  useEffect(() => {
    // Seleciona a primeira data futura com disponibilidade real
    if (calendarData?.dates && calendarData.dates.length > 0 && !selectedDate) {
      const todayDateStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());

      const availableDate =
        calendarData.dates.find((d) => d.available && d.date >= todayDateStr) ||
        calendarData.dates.find((d) => d.available) ||
        calendarData.dates.find((d) => d.date >= todayDateStr) ||
        calendarData.dates[0];

      if (availableDate) {
        setSelectedDate(availableDate.date);
      }
    } else if (!selectedDate) {
      const todayDateStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
      setSelectedDate(todayDateStr);
    }
  }, [calendarData, selectedDate]);

  useEffect(() => {
    return () => {
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
        paymentPollingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !user) return;
    let isMounted = true;
    setIsLoadingPatientData(true);

    Promise.all([
      patientsApi.getProfile().catch(() => null),
      patientsApi.getDependents(1, 50).catch(() => null),
    ])
      .then(([profile, depRes]) => {
        if (!isMounted) return;
        if (profile) setPatientProfile(profile);
        if (depRes?.dependents) {
          setDependents(depRes.dependents);
          if (depRes.dependents.length > 0) {
            setSelectedDependentId((prev) => prev || depRes.dependents[0]?.id || '');
          }
        }
      })
      .finally(() => {
        if (isMounted) setIsLoadingPatientData(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, user]);

  useEffect(() => {
    if (attendeeType === 'dependent' && dependents.length > 0 && !selectedDependentId) {
      const firstDep = dependents[0];
      if (firstDep) {
        setSelectedDependentId(firstDep.id);
      }
    }
  }, [attendeeType, dependents, selectedDependentId]);

  const holderAge = calculateAge(patientProfile?.birthDate);
  const holderName = patientProfile?.name || user?.name || user?.email || 'Titular';

  const selectedDependent = dependents.find((d) => d.id === selectedDependentId);
  const selectedDependentAge = calculateAge(selectedDependent?.birthDate);

  const currentAttendeeAge = attendeeType === 'self' ? holderAge : selectedDependentAge;
  const currentAttendeeName = attendeeType === 'self' ? holderName : (selectedDependent?.name || 'Dependente');

  const minAge = doctor.minAge !== undefined && doctor.minAge !== null ? Number(doctor.minAge) : null;
  const maxAge = doctor.maxAge !== undefined && doctor.maxAge !== null ? Number(doctor.maxAge) : null;

  let isAgeOutOfRange = false;
  let ageWarningMessage: string | null = null;

  if (currentAttendeeAge !== null) {
    if (minAge !== null && maxAge !== null) {
      if (currentAttendeeAge < minAge || currentAttendeeAge > maxAge) {
        isAgeOutOfRange = true;
        ageWarningMessage = `Atenção: Este profissional atende apenas pacientes de ${minAge} a ${maxAge} anos. O paciente selecionado tem ${currentAttendeeAge} anos.`;
      }
    } else if (minAge !== null) {
      if (currentAttendeeAge < minAge) {
        isAgeOutOfRange = true;
        ageWarningMessage = `Atenção: Este profissional atende apenas pacientes de ${minAge} anos ou mais. O paciente selecionado tem ${currentAttendeeAge} anos.`;
      }
    } else if (maxAge !== null) {
      if (currentAttendeeAge > maxAge) {
        isAgeOutOfRange = true;
        ageWarningMessage = `Atenção: Este profissional atende apenas pacientes de até ${maxAge} anos. O paciente selecionado tem ${currentAttendeeAge} anos.`;
      }
    }
  }

  // Preço base da consulta considerando a modalidade selecionada (Telemedicina, Presencial, Domiciliar)
  const modalityPrice = useMemo(() => {
    if (modality === 'IN_PERSON' && doctor.inPersonPrice) return Number(doctor.inPersonPrice);
    if (modality === 'HOME' && doctor.homeCarePrice) return Number(doctor.homeCarePrice);
    if (modality === 'TELEMEDICINE' && doctor.telemedicinePrice) return Number(doctor.telemedicinePrice);
    if (doctor.startingPrice) return Number(doctor.startingPrice);
    if (doctor.consultationPrice) {
      return doctor.consultationPrice >= 1000 ? doctor.consultationPrice / 100 : doctor.consultationPrice;
    }
    return 150.00;
  }, [modality, doctor]);

  const fallbackDoctorPrice = modalityPrice;
  const formattedDoctorPrice = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(fallbackDoctorPrice);

  const { data: installmentData } = useQuery({
    queryKey: ['pagbank', 'installments', fallbackDoctorPrice],
    queryFn: () => paymentsApi.getPagBankInstallmentOptions(fallbackDoctorPrice),
    enabled: fallbackDoctorPrice > 0,
  });

  const selectedModality = MODALITIES.find((m) => m.id === modality);
  const effectiveSlots = useMemo(() => {
    let rawList: any[] = [];

    // 1. Prioriza os slots retornados pela query de slots do dia selecionado
    if (slotsData?.slots && slotsData.slots.length > 0) {
      rawList = slotsData.slots;
    } else if (calendarData?.dates && selectedDate) {
      // 2. Se o backend calendarData já trouxe os slots do dia selecionado
      const matched = calendarData.dates.find((d) => d.date === selectedDate);
      if (matched?.slots && Array.isArray(matched.slots) && matched.slots.length > 0) {
        rawList = matched.slots.map((s: any) => ({
          id: s.id || `slot-${s.startTime?.replace(':', '')}`,
          startTime: s.startTime,
          endTime: s.endTime || '',
          available: (s.status === 'AVAILABLE' || s.available) && !s.isBlocked,
        }));
      }
    }

    // Fuso horário oficial de São Paulo (Brasil)
    const spTodayStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());

    const nowSpTimeStr = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date());

    const isToday = selectedDate === spTodayStr;

    // Se temos slots do backend para esta data:
    if (rawList.length > 0) {
      return rawList.map((slot) => {
        // Bloqueia e desabilita horários que já passaram hoje no fuso de São Paulo
        const isPast = isToday && slot.startTime <= nowSpTimeStr;
        return {
          ...slot,
          available: isPast ? false : Boolean(slot.available),
        };
      });
    }

    // Se o médico nunca cadastrou regras específicas e não temos dados do backend:
    if (selectedDate && (!calendarData || calendarData.dates?.length === 0)) {
      const parts = selectedDate.split('-').map(Number);
      const targetDate = new Date(Date.UTC(parts[0] || 2026, (parts[1] || 1) - 1, parts[2] || 1));
      const dayOfWeek = targetDate.getUTCDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      if (!isWeekend) {
        const defaultTimes = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];
        return defaultTimes.map((time) => {
          const isPast = isToday && time <= nowSpTimeStr;
          return {
            id: `slot-${time.replace(':', '')}`,
            startTime: time,
            endTime: `${time.slice(0, 2)}:30`,
            available: !isPast,
          };
        });
      }
    }

    // Para dias sem atendimento cadastrado pelo médico ou sem vagas:
    return [];
  }, [slotsData, calendarData, selectedDate]);

  const selectedSlot = effectiveSlots.find((s: any) => s.id === selectedSlotId);

  const startPaymentPolling = (paymentId: string) => {
    if (paymentPollingRef.current) {
      clearInterval(paymentPollingRef.current);
    }
    paymentPollingRef.current = setInterval(async () => {
      try {
        const status = await paymentsApi.getPaymentStatus(paymentId);
        if (status.status === 'APPROVED' || status.status === 'PAID') {
          if (paymentPollingRef.current) {
            clearInterval(paymentPollingRef.current);
            paymentPollingRef.current = null;
          }
          setPaymentConfirmed(true);
        }
      } catch {
        // keep polling on transient failures
      }
    }, 4000);
  };

  const resetPaymentState = () => {
    setPixData(null);
    setPaymentConfirmed(false);
    setPaymentError(null);
    setNewAppointmentId(null);
    setCardNumber('');
    setCardHolder('');
    setCardExp('');
    setCardCvv('');
    setCurrentStep(1);
    if (paymentPollingRef.current) {
      clearInterval(paymentPollingRef.current);
      paymentPollingRef.current = null;
    }
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedSlotId(null);
    setError(null);
  };

  const handleSlotSelect = (slotId: string) => {
    setSelectedSlotId(slotId);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    if (!selectedSlotId) {
      setError('Por favor, selecione um horário para a consulta.');
      return;
    }

    if (attendeeType === 'dependent' && !selectedDependentId) {
      setError('Por favor, selecione um dependente para a consulta.');
      return;
    }

    if (isAgeOutOfRange) {
      setError(ageWarningMessage || 'Idade do paciente fora da faixa permitida para este profissional.');
      return;
    }

    if (!chiefComplaint.trim()) {
      setError('Por favor, informe a queixa principal ou motivo da consulta.');
      return;
    }

    setError(null);
    setPaymentError(null);

    const serviceType: ServiceType = selectedModality?.serviceType ?? 'TELEMEDICINA';
    const scheduledAtIso =
      selectedDate && selectedSlot?.startTime
        ? `${selectedDate}T${selectedSlot.startTime}:00.000Z`
        : undefined;

    try {
      const appointment = await createAppointment.mutateAsync({
        doctorId: doctor.id,
        slotId: selectedSlotId,
        scheduledAt: scheduledAtIso,
        type: 'SCHEDULED',
        reason: chiefComplaint.trim(),
        notes: chiefComplaint.trim(),
        serviceType,
        dependentId: attendeeType === 'dependent' ? selectedDependentId : undefined,
      });

      if (!appointment?.id) {
        throw new Error('Não foi possível obter o ID da consulta criada.');
      }

      setNewAppointmentId(appointment.id);

      const patientId = user.id;
      const patientName =
        attendeeType === 'dependent' && selectedDependent
          ? selectedDependent.name
          : (patientProfile?.name || user.name || user.email || 'Paciente');
      const patientEmail = user.email || 'paciente@medikei.com.br';

      const rawAppointmentPrice = Number(appointment?.price ?? 0);
      const appointmentPrice = rawAppointmentPrice >= 1000
        ? rawAppointmentPrice / 100
        : rawAppointmentPrice;
      const calculatedAmount = appointmentPrice > 0 ? appointmentPrice : fallbackDoctorPrice;
      const paymentAmount = calculatedAmount > 0 ? calculatedAmount : 150;

      try {
        if (paymentMethod === 'card') {
          const cardRes: PaymentResult = await paymentsApi.createCardPayment({
            appointmentId: appointment.id,
            patientId,
            doctorId: doctor.id,
            amount: paymentAmount,
            description: `Consulta com ${doctor.profile?.displayName || 'Médico'}`,
            patientName: cardHolder || patientName,
            patientEmail,
            installments: selectedInstallment,
            captchaToken,
          });

          if (cardRes?.checkoutUrl) {
            setTimeout(() => {
              window.location.href = cardRes.checkoutUrl!;
            }, 800);
          } else if (cardRes?.paymentId) {
            await paymentsApi.confirmPayment(cardRes.paymentId).catch(() => {});
            setPaymentConfirmed(true);
          }
        } else {
          const payRes: PaymentResult = await paymentsApi.createPixPayment({
            appointmentId: appointment.id,
            patientId,
            doctorId: doctor.id,
            amount: paymentAmount,
            description: `Consulta com ${doctor.profile?.displayName || 'Médico'}`,
            patientName,
            patientEmail,
            captchaToken,
            returnUrl: `${window.location.origin}/agendamentos/${appointment.id}`,
          });

          setPixData({
            paymentId: payRes.paymentId,
            qrCode: payRes.qrCode,
            qrCodeUrl: payRes.qrCodeUrl,
            copyPaste: payRes.qrCode || payRes.checkoutUrl,
          });

          startPaymentPolling(payRes.paymentId);

          // Se checkoutUrl foi retornado pelo PagBank, redireciona o paciente com segurança
          if (payRes.checkoutUrl) {
            setTimeout(() => {
              window.location.href = payRes.checkoutUrl!;
            }, 800);
          }
        }
      } catch (payErr: unknown) {
        const message =
          payErr instanceof Error
            ? payErr.message
            : 'Não foi possível iniciar o pagamento. Tente novamente.';
        setPaymentError(message);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Ocorreu um erro ao agendar a consulta. Tente novamente.';
      setError(message);
    }
  };

  const handlePaymentDone = () => {
    onClose();
    resetPaymentState();
    if (newAppointmentId) {
      navigate(`/agendamentos/${newAppointmentId}`);
    } else {
      navigate('/agendamentos');
    }
  };

  const handleClose = () => {
    onClose();
    resetPaymentState();
  };

  const doctorName = doctor.profile?.displayName || 'Médico';
  const specialtyName = doctor.primarySpecialty?.name || 'Especialista';

  const showPaymentStep = !!newAppointmentId;
  const showSuccessStep = paymentConfirmed;

  const isStep1Valid = !isAgeOutOfRange && (attendeeType === 'self' || !!selectedDependentId);
  const isStep2Valid = !!selectedDate && !!selectedSlotId;

  const stepLabels = [
    { number: 1, title: 'Paciente & Modalidade' },
    { number: 2, title: 'Data & Horário' },
    { number: 3, title: 'Revisão & Pagamento' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0 overflow-hidden rounded-2xl border border-border shadow-2xl bg-card">
        {/* Header fixo com stepper e botão de fechar */}
        <div className="border-b border-border bg-card px-5 sm:px-6 py-4 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                {showSuccessStep
                  ? 'Agendamento Confirmado'
                  : showPaymentStep
                  ? 'Pagamento da Consulta'
                  : 'Agendar Consulta'}
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                {doctorName} &bull; {specialtyName}
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors"
              aria-label="Fechar modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Stepper discreto (apenas quando não estiver na tela de pagamento ou sucesso) */}
          {!showPaymentStep && !showSuccessStep && (
            <div className="mt-4 pt-3.5 border-t border-border/60">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-xs font-bold text-primary flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-extrabold">
                    {currentStep}
                  </span>
                  <span>Passo {currentStep} de 3:</span>{' '}
                  <span className="font-semibold text-foreground">
                    {stepLabels[currentStep - 1]?.title}
                  </span>
                </span>
                <span className="text-[11px] text-muted-foreground font-medium hidden sm:inline-block">
                  {currentStep === 1 && 'Próximo: Data & Horário'}
                  {currentStep === 2 && 'Próximo: Revisão & Pagamento'}
                  {currentStep === 3 && 'Etapa final'}
                </span>
              </div>

              {/* Barra de progresso visual */}
              <div className="grid grid-cols-3 gap-2">
                {stepLabels.map((s) => {
                  const isDone = s.number < currentStep;
                  const isCurrent = s.number === currentStep;

                  return (
                    <button
                      key={s.number}
                      type="button"
                      disabled={s.number > currentStep}
                      onClick={() => {
                        if (s.number < currentStep) {
                          setCurrentStep(s.number as 1 | 2 | 3);
                        }
                      }}
                      className={cn(
                        'flex items-center gap-1.5 py-1 px-1 rounded-lg text-left transition-all',
                        isDone && 'cursor-pointer hover:opacity-80',
                        !isDone && 'cursor-default'
                      )}
                    >
                      <div
                        className={cn(
                          'h-2 w-full rounded-full transition-all duration-300',
                          isDone
                            ? 'bg-primary'
                            : isCurrent
                            ? 'bg-primary shadow-sm ring-2 ring-primary/20'
                            : 'bg-muted/80'
                        )}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Área central com scroll suave */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-6 scroll-smooth text-foreground">
          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm rounded-xl">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {paymentError && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm rounded-xl">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{paymentError}</span>
            </div>
          )}

          {/* Tela de Sucesso */}
          {showSuccessStep && (
            <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  Pagamento aprovado!
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                  Sua consulta com {doctorName} foi confirmada com sucesso. Todos os detalhes foram enviados para o seu e-mail.
                </p>
              </div>
              <Button onClick={handlePaymentDone} className="mt-2 px-6">
                Ver agendamento
              </Button>
            </div>
          )}

          {/* Tela de Pagamento PIX */}
          {showPaymentStep && !showSuccessStep && paymentMethod === 'pix' && (
            <div className="space-y-5 py-2">
              <div className="text-center">
                <h3 className="text-base font-semibold text-foreground">
                  Realize o pagamento via PIX
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Valor a pagar: <strong className="text-foreground">{formattedDoctorPrice}</strong>
                </p>
              </div>
              {pixData ? (
                <PixQRCode
                  qrCode={pixData.qrCode}
                  qrCodeUrl={pixData.qrCodeUrl}
                  copyPaste={pixData.copyPaste}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Gerando QR Code PIX...</p>
                </div>
              )}
              <div className="p-3 bg-muted/40 rounded-xl text-center">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  A aprovação é imediata. Assim que o pagamento for detectado, confirmaremos sua consulta.
                </p>
              </div>
            </div>
          )}

          {/* Tela de Pagamento Cartão pós-agendamento */}
          {showPaymentStep && !showSuccessStep && paymentMethod === 'card' && (
            <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <h3 className="text-base font-semibold text-foreground">
                Processando pagamento com cartão...
              </h3>
              <p className="text-xs text-muted-foreground max-w-xs">
                Por favor, aguarde enquanto validamos os dados junto à operadora.
              </p>
            </div>
          )}

          {/* FLUXO MULTI-STEP */}
          {!showPaymentStep && !showSuccessStep && (
            <>
              {/* PASSO 1: Paciente & Modalidade */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  {/* Quem será atendido */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-foreground">
                      Quem será atendido nesta consulta?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <label
                        className={cn(
                          'flex items-start gap-3.5 p-4 rounded-2xl border cursor-pointer transition-all duration-150',
                          attendeeType === 'self'
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/25 shadow-sm'
                            : 'border-border bg-card text-foreground hover:bg-muted/40 hover:border-border/80'
                        )}
                      >
                        <input
                          type="radio"
                          name="attendeeType"
                          value="self"
                          checked={attendeeType === 'self'}
                          onChange={() => setAttendeeType('self')}
                          className="mt-1 text-primary focus:ring-primary h-4 w-4"
                        />
                        <div className="flex-1 text-sm">
                          <div className="font-bold text-foreground flex items-center justify-between">
                            <span>Para mim</span>
                            {attendeeType === 'self' && (
                              <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                <Check className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {holderName} {holderAge !== null ? `(${holderAge} anos)` : ''}
                          </div>
                        </div>
                      </label>

                      <label
                        className={cn(
                          'flex items-start gap-3.5 p-4 rounded-2xl border cursor-pointer transition-all duration-150',
                          attendeeType === 'dependent'
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/25 shadow-sm'
                            : 'border-border bg-card text-foreground hover:bg-muted/40 hover:border-border/80'
                        )}
                      >
                        <input
                          type="radio"
                          name="attendeeType"
                          value="dependent"
                          checked={attendeeType === 'dependent'}
                          onChange={() => setAttendeeType('dependent')}
                          className="mt-1 text-primary focus:ring-primary h-4 w-4"
                        />
                        <div className="flex-1 text-sm">
                          <div className="font-bold text-foreground flex items-center justify-between">
                            <span>Para um dependente</span>
                            {attendeeType === 'dependent' && (
                              <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                <Check className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {isLoadingPatientData
                              ? 'Carregando dependentes...'
                              : dependents.length > 0
                              ? `${dependents.length} dependente(s) cadastrado(s)`
                              : 'Nenhum dependente cadastrado'}
                          </div>
                        </div>
                      </label>
                    </div>

                    {attendeeType === 'dependent' && (
                      <div className="mt-3 p-3.5 bg-muted/30 rounded-xl border border-border space-y-2">
                        <label className="block text-xs font-semibold text-foreground">
                          Selecione o dependente:
                        </label>
                        {dependents.length > 0 ? (
                          <select
                            value={selectedDependentId}
                            onChange={(e) => setSelectedDependentId(e.target.value)}
                            className="w-full rounded-xl border border-input bg-background p-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {dependents.map((dep) => {
                              const depAge = calculateAge(dep.birthDate);
                              const ageStr = depAge !== null ? `${depAge} anos` : 'Idade não informada';
                              return (
                                <option key={dep.id} value={dep.id}>
                                  {dep.name} ({dep.relationship} - {ageStr})
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <div className="p-3 bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-200 text-xs rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <span>Você ainda não possui dependentes cadastrados.</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                onClose();
                                navigate('/dependentes');
                              }}
                              className="text-xs h-7 self-start sm:self-auto"
                            >
                              Cadastrar dependente
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Validação Preventiva de Faixa Etária */}
                    {isAgeOutOfRange && ageWarningMessage && (
                      <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-200 text-sm rounded-xl">
                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <p className="font-semibold text-amber-950 dark:text-amber-100">
                            {ageWarningMessage}
                          </p>
                          <p className="text-xs text-amber-900/80 dark:text-amber-300/80">
                            Para prosseguir com o agendamento, selecione outro paciente compatível com a faixa etária atendida por este médico.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Modalidade de atendimento */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-foreground">
                      Modalidade de atendimento
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {MODALITIES.map((item) => {
                        const Icon = item.icon;
                        const isSelected = modality === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setModality(item.id)}
                            className={cn(
                              'flex flex-col items-center justify-center p-4 rounded-2xl border text-center transition-all duration-150 cursor-pointer relative',
                              isSelected
                                ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/25 shadow-sm scale-[1.02]'
                                : 'border-border bg-card text-foreground hover:bg-muted/40 hover:border-border/80'
                            )}
                          >
                            <div
                              className={cn(
                                'w-11 h-11 rounded-xl flex items-center justify-center mb-2.5 transition-colors',
                                isSelected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                              )}
                            >
                              <Icon className="h-5 w-5" />
                            </div>
                            <span className="text-sm font-bold">{item.label}</span>
                            <span className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {item.description}
                            </span>
                            {isSelected && (
                              <div className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-primary" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* PASSO 2: Data & Horário */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarIcon className="w-4 h-4 text-primary" />
                      <h4 className="text-sm font-semibold text-foreground">
                        Selecione a data da consulta
                      </h4>
                    </div>
                    {isLoadingCalendar ? (
                      <div className="h-64 bg-muted rounded-2xl animate-pulse" />
                    ) : (
                      <CalendarView
                        availability={calendarData?.dates || []}
                        selectedDate={selectedDate}
                        onSelectDate={handleDateSelect}
                        from={today}
                        to={thirtyDaysFromNow}
                      />
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <h4 className="text-sm font-semibold text-foreground">
                          Horários disponíveis
                        </h4>
                      </div>
                      {selectedDate && (
                        <span className="text-xs font-medium text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-lg">
                          {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      )}
                    </div>

                    {selectedDate ? (
                      <TimeSlotPicker
                        slots={effectiveSlots}
                        selectedSlotId={selectedSlotId}
                        onSelectSlot={handleSlotSelect}
                        isLoading={isLoadingSlots}
                      />
                    ) : (
                      <div className="p-6 text-center border border-dashed border-border rounded-xl">
                        <p className="text-sm text-muted-foreground">
                          Selecione um dia no calendário acima para visualizar os horários.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PASSO 3: Revisão & Pagamento */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {/* Card de Resumo com destaque para o Preço */}
                  <div className="bg-card rounded-2xl border border-border p-5 space-y-4 shadow-sm">
                    <div className="flex items-center justify-between border-b border-border/80 pb-4">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Valor da Consulta
                        </span>
                        <div className="text-2xl sm:text-3xl font-black text-primary tracking-tight">
                          {formattedDoctorPrice}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                          {selectedModality?.label}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                      <div className="flex items-start gap-2.5 text-muted-foreground bg-muted/20 p-2.5 rounded-xl border border-border/40">
                        <div className="p-1.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <User className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <span className="font-bold text-foreground block truncate">
                            {doctorName}
                          </span>
                          <span className="text-xs">{specialtyName} {doctor.crm ? `• CRM ${doctor.crm}` : ''}</span>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5 text-muted-foreground bg-muted/20 p-2.5 rounded-xl border border-border/40">
                        <div className="p-1.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <User className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <span className="font-bold text-foreground block truncate">
                            Paciente: {currentAttendeeName}
                          </span>
                          <span className="text-xs">
                            {attendeeType === 'self' ? 'Titular' : 'Dependente'}{' '}
                            {currentAttendeeAge !== null ? `(${currentAttendeeAge} anos)` : ''}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5 text-muted-foreground bg-muted/20 p-2.5 rounded-xl border border-border/40">
                        <div className="p-1.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <CalendarIcon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <span className="font-bold text-foreground block">Data</span>
                          <span className="text-xs">
                            {selectedDate
                              ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : '--'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5 text-muted-foreground bg-muted/20 p-2.5 rounded-xl border border-border/40">
                        <div className="p-1.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <Clock className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <span className="font-bold text-foreground block">Horário</span>
                          <span className="text-xs">
                            {selectedSlot ? `${selectedSlot.startTime} às ${selectedSlot.endTime}` : '--'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Queixa principal */}
                  <div className="space-y-2">
                    <label
                      htmlFor="chiefComplaint"
                      className="block text-sm font-semibold text-foreground"
                    >
                      Motivo da consulta / Queixa principal <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="chiefComplaint"
                      rows={3}
                      value={chiefComplaint}
                      onChange={(e) => {
                        setChiefComplaint(e.target.value);
                        if (error) setError(null);
                      }}
                      placeholder="Descreva brevemente o que você está sentindo ou o objetivo da consulta..."
                      className="w-full rounded-2xl border border-input bg-background p-3.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all resize-none shadow-xs"
                    />
                  </div>

                  {/* Forma de pagamento */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-foreground">
                      Forma de pagamento
                    </label>
                    <div className="grid grid-cols-2 gap-3.5">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('pix')}
                        className={cn(
                          'p-4 rounded-2xl border text-center transition-all duration-150 cursor-pointer text-sm font-bold flex items-center justify-center gap-2 select-none',
                          paymentMethod === 'pix'
                            ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/25 shadow-sm scale-[1.02]'
                            : 'border-border bg-card text-foreground hover:bg-muted/40 hover:border-border/80'
                        )}
                      >
                        <span>PIX</span>
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-extrabold px-2 py-0.5 rounded-full border border-emerald-500/20">
                          Instantâneo
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        className={cn(
                          'p-4 rounded-2xl border text-center transition-all duration-150 cursor-pointer text-sm font-bold flex items-center justify-center gap-2 select-none',
                          paymentMethod === 'card'
                            ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/25 shadow-sm scale-[1.02]'
                            : 'border-border bg-card text-foreground hover:bg-muted/40 hover:border-border/80'
                        )}
                      >
                        <CreditCard className="h-4 w-4" />
                        Cartão
                      </button>
                    </div>

                    {paymentMethod === 'card' && (
                      <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3 mt-3">
                        <div>
                          <label className="block text-xs font-semibold text-foreground mb-1">
                            Número do cartão
                          </label>
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                            placeholder="0000 0000 0000 0000"
                            className="w-full rounded-lg border border-input bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-foreground mb-1">
                            Nome impresso no cartão
                          </label>
                          <input
                            type="text"
                            value={cardHolder}
                            onChange={(e) => setCardHolder(e.target.value)}
                            placeholder="Nome do titular"
                            className="w-full rounded-lg border border-input bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-foreground mb-1">
                              Validade
                            </label>
                            <input
                              type="text"
                              value={cardExp}
                              onChange={(e) => setCardExp(e.target.value)}
                              placeholder="MM/AA"
                              className="w-full rounded-lg border border-input bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground mb-1">
                              CVV
                            </label>
                            <input
                              type="text"
                              value={cardCvv}
                              onChange={(e) => setCardCvv(e.target.value)}
                              placeholder="123"
                              className="w-full rounded-lg border border-input bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-foreground mb-1">
                            Opções de Parcelamento
                          </label>
                          <select
                            value={selectedInstallment}
                            onChange={(e) => setSelectedInstallment(Number(e.target.value))}
                            className="w-full rounded-lg border border-input bg-background p-2.5 text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                          >
                            {installmentData?.options && installmentData.options.length > 0 ? (
                              installmentData.options.map((opt) => (
                                <option key={opt.numero} value={opt.numero}>
                                  {opt.descricao}
                                </option>
                              ))
                            ) : (
                              <option value={1}>1x de {formattedDoctorPrice} (sem juros)</option>
                            )}
                          </select>
                        </div>
                      </div>
                    )}
                    <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Rodapé fixo (Sticky Footer) */}
        {!showPaymentStep && !showSuccessStep && (
          <div className="p-4 sm:px-6 border-t border-border bg-card/95 backdrop-blur-sm shrink-0 flex items-center justify-between gap-3">
            {currentStep === 1 ? (
              <Button
                variant="outline"
                type="button"
                onClick={handleClose}
                disabled={createAppointment.isPending}
                className="rounded-xl font-medium"
              >
                Cancelar
              </Button>
            ) : (
              <Button
                variant="outline"
                type="button"
                onClick={() => setCurrentStep((prev) => (prev > 1 ? ((prev - 1) as 1 | 2 | 3) : 1))}
                disabled={createAppointment.isPending}
                className="flex items-center gap-1.5 rounded-xl font-semibold hover:bg-muted"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
            )}

            {currentStep === 1 && (
              <Button
                type="button"
                onClick={() => setCurrentStep(2)}
                disabled={!isStep1Valid}
                className="flex items-center gap-1.5 px-6 rounded-xl font-bold shadow-md shadow-primary/20"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 2 && (
              <Button
                type="button"
                onClick={() => setCurrentStep(3)}
                disabled={!isStep2Valid}
                className="flex items-center gap-1.5 px-6 rounded-xl font-bold shadow-md shadow-primary/20"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 3 && (
              <Button
                type="button"
                onClick={handleConfirm}
                isLoading={createAppointment.isPending}
                disabled={!chiefComplaint.trim() || createAppointment.isPending}
                className="px-7 rounded-xl font-bold shadow-lg shadow-primary/25 hover:shadow-xl"
              >
                Confirmar Agendamento
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
